// O perfil na tela (espec. 8.3): tabela `defasagem × percentual`, botão de nova linha,
// soma ao vivo e a mensagem do validador. É SEMPRE ela que mostra o perfil — os atalhos
// só a preenchem e o "Personalizado" a torna editável. Apresentacional: as linhas
// chegam prontas, com o `lineId` que serve de chave.
import { bpsToPercentInput, integerInput, percentInputToBps, percentLabel } from "./format";
import { AT_SIGHT_PROFILE, MAX_LINES, MAX_OFFSET, MIN_OFFSET } from "./profile";

// O erro do perfil descreve os campos de percentual (espec. 8.5) — este id é o alvo
// do `aria-describedby` daqui e do `RuleShortcutFields`.
export const PROFILE_ERROR_ID = "ampli-rule-editor-profile-error";

/**
 * As linhas editáveis carregam um id gerado, estável e único, usado como chave do React.
 * Nem o índice do array nem `offsetMonths` servem: a tabela do "Personalizado" aceita
 * duplicata transitória de defasagem enquanto o cliente digita, e remover/reordenar
 * linhas com chave por posição faz o React reaproveitar o input errado.
 */
let lineSequence = 0;
export const withLineId = (line) => {
  lineSequence += 1;
  return {
    lineId: `line-${lineSequence}`,
    offsetMonths: line?.offsetMonths,
    percentBps: line?.percentBps,
  };
};

export const seedLines = (profile) => (
  Array.isArray(profile) && profile.length > 0 ? profile : AT_SIGHT_PROFILE
).map(withLineId);

const sumOf = (lines) => lines.reduce(
  (total, line) => total + (Number.isFinite(line.percentBps) ? line.percentBps : 0),
  0,
);

const RuleProfileTable = ({
  lines,
  editable,
  message,
  summary,
  onChangeLine,
  onRemoveLine,
  onAddLine,
}) => {
  const errorId = message ? PROFILE_ERROR_ID : undefined;
  return (
    <>
      <div
        className="ampli-rule-editor__table"
        role="region"
        aria-label="Perfil da regra"
        tabIndex={0}
      >
        <table aria-label="Percentual por defasagem">
          <thead>
            <tr>
              <th>Defasagem (meses)</th>
              <th>Percentual</th>
              {editable ? <th>Ações</th> : null}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={line.lineId} data-line-id={line.lineId}>
                <td>
                  {editable ? (
                    <input
                      type="number"
                      aria-label={`Defasagem da linha ${index + 1}`}
                      min={String(MIN_OFFSET)}
                      max={String(MAX_OFFSET)}
                      value={Number.isFinite(line.offsetMonths) ? line.offsetMonths : ""}
                      onChange={(event) => onChangeLine(line.lineId, {
                        offsetMonths: integerInput(event.target.value),
                      })}
                    />
                  ) : line.offsetMonths}
                </td>
                <td>
                  {editable ? (
                    <input
                      type="number"
                      aria-label={`Percentual da linha ${index + 1}`}
                      aria-describedby={errorId}
                      value={bpsToPercentInput(line.percentBps)}
                      onChange={(event) => onChangeLine(line.lineId, {
                        percentBps: percentInputToBps(event.target.value),
                      })}
                    />
                  ) : percentLabel(line.percentBps)}
                </td>
                {editable ? (
                  <td>
                    <button
                      type="button"
                      className="button button-secondary small"
                      aria-label={`Remover linha ${index + 1}`}
                      onClick={() => onRemoveLine(line.lineId)}
                    >
                      Remover
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editable ? (
        <button
          type="button"
          className="button button-secondary small"
          disabled={lines.length >= MAX_LINES}
          onClick={onAddLine}
        >
          Adicionar linha
        </button>
      ) : null}

      <p className="ampli-rule-editor__sum" aria-live="polite">
        {`Soma: ${percentLabel(sumOf(lines))}`}
      </p>
      {message ? (
        <p className="alert alert-error" role="alert" id={PROFILE_ERROR_ID}>{message}</p>
      ) : (
        <p className="ampli-rule-editor__summary">{summary}</p>
      )}
    </>
  );
};

export default RuleProfileTable;
